/* eslint-disable @typescript-eslint/no-unused-vars, react-hooks/exhaustive-deps, import/no-anonymous-default-export, no-useless-escape */
import React,{useState,useEffect,useCallback,useRef} from 'react';
import Layout from '../components/layout/Layout';
import StatCard from '../components/common/StatCard';
import SlidePanel from '../components/common/SlidePanel';
import {Select} from '../components/common/FormField';
import PatientSearchInput from '../components/patient/PatientSearchInput';
import {useAuth} from '../hooks/useAuth';
import api from '../services/api';
import {fmtDateTime,safeArray} from '../utils/helpers';

const TABS=[['indents','📋 Indents'],['new','➕ New Indent']];


const INDENT_TYPES=['PHARMACY','STORES','LAB','RADIOLOGY'];
const URGENCY_META:Record<string,string>={
  ROUTINE:'bg-slate-100 text-slate-600',
  URGENT:'bg-amber-100 text-amber-700',
  EMERGENCY:'bg-red-600 text-white',
};
const STATUS_META:Record<string,string>={
  PENDING:'bg-yellow-100 text-yellow-700',
  APPROVED:'bg-blue-100 text-[#02357A]',
  PARTIALLY_FULFILLED:'bg-orange-100 text-orange-700',
  FULFILLED:'bg-green-100 text-green-700',
  REJECTED:'bg-red-100 text-red-700',
  CANCELLED:'bg-gray-100 text-gray-500',
};

/* ─── Drug Search (pharmacy items) ──────────────────────────────────────── */
function DrugSearchInput({onSelect}:{onSelect:(d:any)=>void}){
  const [q,setQ]=useState('');
  const [results,setResults]=useState<any[]>([]);
  const [loading,setLoading]=useState(false);
  const [open,setOpen]=useState(false);
  const ref=useRef<HTMLDivElement>(null);

  useEffect(()=>{
    const h=(e:MouseEvent)=>{if(ref.current&&!ref.current.contains(e.target as Node))setOpen(false);};
    document.addEventListener('mousedown',h);return()=>document.removeEventListener('mousedown',h);
  },[]);
  useEffect(()=>{
    if(q.length<2){setResults([]);setOpen(false);return;}
    const t=setTimeout(async()=>{
      setLoading(true);
      try{
        const r=await api.getDrugs({search:q,take:10});
        setResults(Array.isArray(r.data?.drugs)?r.data.drugs:safeArray(r.data,['drugs']));
        setOpen(true);
      }catch{setResults([]);}finally{setLoading(false);}
    },300);
    return()=>clearTimeout(t);
  },[q]);

  return(
    <div ref={ref} className="relative">
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">💊</span>
        <input className="input pl-9" value={q} onChange={e=>setQ(e.target.value)}
          onFocus={()=>q.length>=2&&setOpen(true)} placeholder="Search drug to add to indent..." autoComplete="off"/>
        {loading&&<div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 border-2 border-green-200 border-t-green-600 rounded-full animate-spin"/>}
      </div>
      {open&&results.length>0&&(
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border rounded-xl shadow-2xl overflow-hidden max-h-56 overflow-y-auto">
          {results.map((d:any)=>(
            <div key={d.id} onClick={()=>{onSelect(d);setQ('');setOpen(false);}}
              className="flex items-center gap-3 px-4 py-2.5 hover:bg-green-50 cursor-pointer border-b last:border-0">
              <div className="flex-1">
                <p className="font-semibold text-sm text-slate-800">{d.name}</p>
                <p className="text-xs text-slate-400">{d.genericName} · {d.dosageForm} {d.strength}</p>
              </div>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium
                ${d.totalStock>10?'bg-green-100 text-green-700':d.totalStock>0?'bg-amber-100 text-amber-700':'bg-red-100 text-red-700'}`}>
                Stock: {d.totalStock}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Inventory item search (Stores) ─────────────────────────────────────── */
function ItemSearchInput({onSelect}:{onSelect:(i:any)=>void}){
  const [q,setQ]=useState('');
  const [results,setResults]=useState<any[]>([]);
  const [open,setOpen]=useState(false);
  const ref=useRef<HTMLDivElement>(null);
  useEffect(()=>{
    const h=(e:MouseEvent)=>{if(ref.current&&!ref.current.contains(e.target as Node))setOpen(false);};
    document.addEventListener('mousedown',h);return()=>document.removeEventListener('mousedown',h);
  },[]);
  useEffect(()=>{
    if(q.length<2){setResults([]);setOpen(false);return;}
    const t=setTimeout(async()=>{
      try{
        const r=await api.getInventoryItems({search:q,take:10});
        setResults(safeArray(r.data,['items']));
        setOpen(true);
      }catch{setResults([]);}
    },300);
    return()=>clearTimeout(t);
  },[q]);
  return(
    <div ref={ref} className="relative">
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">📦</span>
        <input className="input pl-9" value={q} onChange={e=>setQ(e.target.value)}
          onFocus={()=>q.length>=2&&setOpen(true)} placeholder="Search store item / consumable..." autoComplete="off"/>
      </div>
      {open&&results.length>0&&(
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border rounded-xl shadow-2xl overflow-hidden max-h-56 overflow-y-auto">
          {results.map((it:any)=>(
            <div key={it.id} onClick={()=>{onSelect(it);setQ('');setOpen(false);}}
              className="flex items-center gap-3 px-4 py-2.5 hover:bg-green-50 cursor-pointer border-b last:border-0">
              <div className="flex-1">
                <p className="font-semibold text-sm text-slate-800">{it.itemName}</p>
                <p className="text-xs text-slate-400">{it.category} · {it.unit}</p>
              </div>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium
                ${it.currentStock>it.reorderLevel?'bg-green-100 text-green-700':'bg-amber-100 text-amber-700'}`}>
                Stock: {it.currentStock}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Print Issue Slip ────────────────────────────────────────────────────── */
function printIssueSlip(indent:any){
  const rows=(indent.items||[]).map((it:any,i:number)=>`
    <tr><td>${i+1}</td><td>${it.itemName||it.drugName}</td><td>${it.quantity}</td>
    <td>${it.fulfilledQuantity||0}</td><td>${it.unit||'—'}</td><td>${it.remarks||'—'}</td></tr>`).join('');
  const html=`<!DOCTYPE html><html><head><title>Issue Slip</title>
  <style>body{font-family:Arial,sans-serif;padding:24px;font-size:13px;}
  h2{text-align:center;border-bottom:2px solid #000;padding-bottom:8px;}
  table{width:100%;border-collapse:collapse;margin-top:12px;}
  th{background:#333;color:#fff;padding:6px;text-align:left;font-size:11px;}
  td{padding:6px;border-bottom:1px solid #eee;}
  .row{display:flex;justify-content:space-between;margin:4px 0;font-size:12px;}
  @media print{body{padding:10px;}}</style></head><body>
  <h2>🏥 Store Issue Slip</h2>
  <div class="row"><span><strong>Indent No:</strong> ${indent.indentNumber}</span><span><strong>Issue Slip:</strong> ${indent.issueSlipNumber||'—'}</span></div>
  <div class="row"><span><strong>Ward:</strong> ${indent.wardName||'—'}</span><span><strong>Type:</strong> ${indent.indentType}</span></div>
  <div class="row"><span><strong>Date:</strong> ${new Date().toLocaleString('en-IN')}</span><span><strong>Urgency:</strong> ${indent.urgency}</span></div>
  <table><thead><tr><th>#</th><th>Item</th><th>Requested</th><th>Issued</th><th>Unit</th><th>Remarks</th></tr></thead>
  <tbody>${rows}</tbody></table>
  <div style="margin-top:40px;display:flex;justify-content:space-between">
    <div style="text-align:center"><div style="border-top:1px solid #000;width:180px;margin-bottom:4px"></div><p>Requested By (Nursing)</p></div>
    <div style="text-align:center"><div style="border-top:1px solid #000;width:180px;margin-bottom:4px"></div><p>Issued By (Stores/Pharmacy)</p></div>
  </div>
  </body></html>`;
  const w=window.open('','_blank','width=800,height=700');
  if(!w)return;
  w.document.write(html);w.document.close();w.onload=()=>w.print();
}

/* ════════════════════════════════════════════════════════════════════════ */
export default function NursingIndentPage(){
  const {user}=useAuth();
  const [indents,setIndents]=useState<any[]>([]);
  const [stats,setStats]=useState<any>({});
  const [wards,setWards]=useState<any[]>([]);
  const [loading,setLoading]=useState(false);
  const [tab,setTab]=useState<string>('indents');
  const [filterStatus,setFilterStatus]=useState('PENDING');
  const [filterType,setFilterType]=useState('');
  const [showForm,setShowForm]=useState(false);
  const [showDetail,setShowDetail]=useState<any>(null);
  const [showReject,setShowReject]=useState<any>(null);
  const [rejectReason,setRejectReason]=useState('');
  const [fulfillQty,setFulfillQty]=useState<Record<number,string>>({});
  const [saving,setSaving]=useState(false);
  const [msg,setMsg]=useState('');

  const [selectedPatient,setSelectedPatient]=useState<any>(null);
  const [form,setForm]=useState({
    indentType:'PHARMACY', wardId:'', wardName:'', urgency:'ROUTINE',
    chargeToPatient:false, remarks:'', items:[] as any[],
  });

  const notify=(m:string)=>{setMsg(m);setTimeout(()=>setMsg(''),4000);};

  const load=useCallback(async()=>{
    setLoading(true);
    try{
      const [i,s]=await Promise.all([
        api.getIndents({status:filterStatus||undefined,indentType:filterType||undefined}),
        api.getIndentStats(),
      ]);
      setIndents(Array.isArray(i.data)?i.data:safeArray(i.data));
      setStats(s.data||{});
    }catch{}finally{setLoading(false);}
  },[filterStatus,filterType]);

  useEffect(()=>{load();},[filterStatus,filterType]);
  useEffect(()=>{api.getWards().then(r=>setWards(Array.isArray(r.data)?r.data:safeArray(r.data,['wards']))).catch(()=>{});},[]);

  const resetForm=()=>{
    setSelectedPatient(null);
    setForm({indentType:'PHARMACY',wardId:'',wardName:'',urgency:'ROUTINE',chargeToPatient:false,remarks:'',items:[]});
  };

  const addDrugItem=(d:any)=>{
    setForm({...form,items:[...form.items,{
      drugId:d.id, itemName:d.name, unit:d.dosageForm||'unit', quantity:1, unitPrice:d.sellingPrice||d.mrp||0, remarks:'',
    }]});
  };
  const addStoreItem=(it:any)=>{
    setForm({...form,items:[...form.items,{
      itemId:it.id, itemName:it.itemName, unit:it.unit||'unit', quantity:1, unitPrice:it.unitCost||0, remarks:'',
    }]});
  };
  const updateItemQty=(i:number,qty:string)=>{
    const copy=[...form.items]; copy[i]={...copy[i],quantity:+qty||0}; setForm({...form,items:copy});
  };
  const removeItem=(i:number)=>setForm({...form,items:form.items.filter((_,idx)=>idx!==i)});

  const save=async()=>{
    if(form.items.length===0){notify('⚠️ Add at least one item');return;}
    if(!form.wardId){notify('⚠️ Select a ward');return;}
    setSaving(true);
    try{
      await api.createIndent({
        ...form,
        patientId:selectedPatient?.id||null,
        wardName:wards.find((w:any)=>w.id===form.wardId)?.name||form.wardName,
      });
      notify('✅ Indent raised successfully!');
      setShowForm(false);resetForm();load();
    }catch(e:any){notify('❌ '+(e.response?.data?.message||'Error'));}
    finally{setSaving(false);}
  };

  const approve=async(id:string)=>{
    try{await api.approveIndent(id);notify('✅ Indent approved');load();}
    catch(e:any){notify('❌ '+(e.response?.data?.message||'Error'));}
  };
  const reject=async()=>{
    if(!showReject)return;
    try{await api.rejectIndent(showReject.id,rejectReason||'Rejected');notify('⚠️ Indent rejected');setShowReject(null);setRejectReason('');load();}
    catch(e:any){notify('❌ '+(e.response?.data?.message||'Error'));}
  };
  const cancel=async(id:string)=>{
    try{await api.cancelIndent(id);notify('✅ Indent cancelled');load();}
    catch(e:any){notify('❌ '+(e.response?.data?.message||'Error'));}
  };

  const openFulfill=(indent:any)=>{
    const qty:Record<number,string>={};
    (indent.items||[]).forEach((it:any,i:number)=>{
      qty[i]=String(Math.max(0,it.quantity-(it.fulfilledQuantity||0)));
    });
    setFulfillQty(qty);
    setShowDetail(indent);
  };

  const fulfill=async()=>{
    if(!showDetail)return;
    setSaving(true);
    try{
      const fulfillments=Object.entries(fulfillQty).map(([idx,qty])=>({itemIndex:+idx,quantity:+qty||0}));
      const r=await api.fulfillIndent(showDetail.id,fulfillments);
      notify('✅ Indent fulfilled — issue slip generated');
      printIssueSlip(r.data);
      setShowDetail(null);load();
    }catch(e:any){notify('❌ '+(e.response?.data?.message||'Error'));}
    finally{setSaving(false);}
  };

  const postToBill=async(id:string)=>{
    try{
      const r=await api.postIndentToBill(id);
      notify(`✅ ₹${r.data.amount.toFixed(2)} posted to patient bill`);
      load();
    }catch(e:any){notify('❌ '+(e.response?.data?.message||'Error'));}
  };

  return(
    <Layout title="Nursing Online Indent" actions={
      <button onClick={()=>{resetForm();setTab('new');}} className="btn-primary text-sm">📋 New Indent</button>
    }>
      {msg&&(
        <div className={`mb-4 p-3 rounded-lg text-sm border ${msg.startsWith('✅')?'bg-green-50 text-green-700 border-green-200':msg.startsWith('⚠️')?'bg-amber-50 text-amber-700 border-amber-200':'bg-red-50 text-red-700 border-red-200'}`}>
          {msg}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        <StatCard label="Pending"    value={stats.pending||0}   icon="⏳" color="amber"/>
        <StatCard label="Approved"   value={stats.approved||0}  icon="✅" color="blue"/>
        <StatCard label="Fulfilled"  value={stats.fulfilled||0} icon="📦" color="green"/>
        <StatCard label="Urgent/Emergency" value={stats.urgent||0} icon="🚨" color="red"/>
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-4 flex-wrap items-center">
        {['PENDING','APPROVED','PARTIALLY_FULFILLED','FULFILLED','REJECTED','CANCELLED'].map(s=>(
          <button key={s} onClick={()=>setFilterStatus(s===filterStatus?'':s)}
            className={`text-xs px-3 py-1.5 rounded-full border transition-all
              ${filterStatus===s?'bg-[#02357A] text-white border-[#015CA6]':'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
            {s.replace('_',' ')}
          </button>
        ))}
        <span className="text-slate-300">|</span>
        {INDENT_TYPES.map(t=>(
          <button key={t} onClick={()=>setFilterType(t===filterType?'':t)}
            className={`text-xs px-3 py-1.5 rounded-full border transition-all
              ${filterType===t?'bg-purple-600 text-white border-purple-600':'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
            {t}
          </button>
        ))}
        <button onClick={load} className="ml-auto text-xs text-[#02357A] hover:underline">🔄 Refresh</button>
      </div>

      {/* List */}
      {loading?(
        <div className="card p-12 text-center"><div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto"/></div>
      ):indents.length===0?(
        <div className="card p-16 text-center"><span className="text-5xl block mb-3">📋</span><p className="text-slate-400">No indents found</p></div>
      ):(
        <div className="space-y-3">
          {indents.map((ind:any,i:number)=>(
            <div key={i} className="card p-4">
              <div className="flex flex-wrap justify-between items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="font-mono font-black text-[#02357A] text-sm">{ind.indentNumber}</span>
                    <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-bold">{ind.indentType}</span>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${URGENCY_META[ind.urgency]||'bg-slate-100'}`}>{ind.urgency}</span>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${STATUS_META[ind.status]||'bg-slate-100'}`}>{ind.status.replace('_',' ')}</span>
                    {ind.chargeToPatient&&<span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">💰 Chargeable</span>}
                  </div>
                  <p className="font-semibold text-slate-700 text-sm">🏥 {ind.wardName||'—'}{ind.patientId&&` · Patient: ${ind.patientId}`}</p>
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {(ind.items||[]).slice(0,4).map((it:any,j:number)=>(
                      <span key={j} className="text-xs bg-slate-50 border border-slate-200 px-2 py-0.5 rounded-full">
                        {it.itemName} × {it.quantity}{it.fulfilledQuantity?` (${it.fulfilledQuantity} issued)`:''}
                      </span>
                    ))}
                    {ind.items?.length>4&&<span className="text-xs text-slate-400">+{ind.items.length-4} more</span>}
                  </div>
                  <p className="text-xs text-slate-400 mt-1.5">{fmtDateTime(ind.createdAt||ind.created_at)}</p>
                </div>
                <div className="flex flex-col gap-1.5 items-end flex-shrink-0">
                  {ind.status==='PENDING'&&(
                    <div className="flex gap-1.5">
                      <button onClick={()=>approve(ind.id)} className="text-xs btn-success py-1 px-2">✓ Approve</button>
                      <button onClick={()=>setShowReject(ind)} className="text-xs btn-danger py-1 px-2">✕ Reject</button>
                    </div>
                  )}
                  {(ind.status==='APPROVED'||ind.status==='PARTIALLY_FULFILLED')&&(
                    <button onClick={()=>openFulfill(ind)} className="text-xs btn-primary py-1 px-3">📦 Fulfill</button>
                  )}
                  {ind.status==='FULFILLED'&&(
                    <div className="flex gap-1.5">
                      <button onClick={()=>printIssueSlip(ind)} className="text-xs btn-secondary py-1 px-2">🖨️ Slip</button>
                      {ind.chargeToPatient&&ind.patientId&&!ind.billPosted&&(
                        <button onClick={()=>postToBill(ind.id)} className="text-xs btn-warning py-1 px-2">💰 Post to Bill</button>
                      )}
                      {ind.billPosted&&<span className="text-xs text-green-600 font-semibold">✓ Billed</span>}
                    </div>
                  )}
                  {ind.status==='PENDING'&&(
                    <button onClick={()=>cancel(ind.id)} className="text-xs text-slate-400 hover:text-red-500">Cancel</button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ═══ NEW INDENT MODAL ═══ */}
      {tab==='new'&&(
        <div className="max-w-3xl">
          <div className="space-y-4">
          {/* Type */}
          <div>
            <label className="label">Indent Type *</label>
            <div className="grid grid-cols-4 gap-2">
              {INDENT_TYPES.map(t=>(
                <button key={t} type="button" onClick={()=>setForm({...form,indentType:t,items:[]})}
                  className={`p-2 rounded-xl border-2 text-sm font-semibold transition-all
                    ${form.indentType===t?'border-[#015CA6] bg-[#02357A] text-white':'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                  {t}
                </button>
              ))}
            </div>
          </div>

          {/* Ward + Urgency */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Ward *</label>
              <Select value={form.wardId} onChange={(e:any)=>setForm({...form,wardId:e.target.value})}>
                <option value="">— Select Ward —</option>
                {wards.map((w:any)=><option key={w.id} value={w.id}>{w.name}</option>)}
              </Select>
            </div>
            <div>
              <label className="label">Urgency</label>
              <Select value={form.urgency} onChange={(e:any)=>setForm({...form,urgency:e.target.value})}>
                {['ROUTINE','URGENT','EMERGENCY'].map(u=><option key={u}>{u}</option>)}
              </Select>
            </div>
          </div>

          {/* Patient (optional, for chargeable items) */}
          <div>
            <PatientSearchInput
              label="Patient (optional — for patient-specific / chargeable items)"
              selected={selectedPatient}
              onSelect={setSelectedPatient}
              onClear={()=>setSelectedPatient(null)}
            />
          </div>
          {selectedPatient&&(
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.chargeToPatient}
                onChange={e=>setForm({...form,chargeToPatient:e.target.checked})} className="w-4 h-4"/>
              <span className="text-sm font-medium text-slate-700">💰 Charge these items to patient's bill</span>
            </label>
          )}

          {/* Item search */}
          <div>
            <label className="label">Add Items *</label>
            {form.indentType==='PHARMACY'
              ? <DrugSearchInput onSelect={addDrugItem}/>
              : <ItemSearchInput onSelect={addStoreItem}/>}
          </div>

          {/* Items list */}
          {form.items.length>0&&(
            <div className="space-y-1.5">
              {form.items.map((it,i)=>(
                <div key={i} className="flex items-center gap-2 bg-slate-50 rounded-lg p-2">
                  <div className="flex-1">
                    <p className="font-semibold text-sm text-slate-800">{it.itemName}</p>
                    {it.unitPrice>0&&<p className="text-xs text-slate-400">₹{it.unitPrice}/{it.unit}</p>}
                  </div>
                  <input type="number" min="1" className="input w-20 text-center" value={it.quantity}
                    onChange={e=>updateItemQty(i,e.target.value)}/>
                  <span className="text-xs text-slate-400 w-12">{it.unit}</span>
                  <button onClick={()=>removeItem(i)} className="text-red-400 hover:text-red-600 px-1">✕</button>
                </div>
              ))}
            </div>
          )}

          <div>
            <label className="label">Remarks</label>
            <textarea className="input" rows={2} value={form.remarks} onChange={e=>setForm({...form,remarks:e.target.value})}
              placeholder="Additional notes for stores/pharmacy..."/>
          </div>

          <div className="flex gap-3">
            <button onClick={save} disabled={saving||form.items.length===0} className="btn-primary flex-1 py-2.5 font-semibold">
              {saving?'Submitting...':'📋 Submit Indent'}
            </button>
            <button onClick={()=>{setShowForm(false);resetForm();}} className="btn-secondary px-6">Cancel</button>
          </div>
        </div>
        </div>
      )}

      {/* ═══ FULFILL MODAL ═══ */}
      <SlidePanel isOpen={!!showDetail} onClose={()=>setShowDetail(null)} title={`📦 Fulfill Indent — ${showDetail?.indentNumber}`} size="lg">
        {showDetail&&(
          <div>
            <div className="mb-4 p-3 bg-slate-50 rounded-xl text-sm">
              <p className="font-semibold">{showDetail.wardName} · {showDetail.indentType}</p>
              <p className="text-xs text-slate-400">Urgency: {showDetail.urgency}</p>
            </div>
            <div className="space-y-2 mb-4">
              {(showDetail.items||[]).map((it:any,i:number)=>{
                const remaining=it.quantity-(it.fulfilledQuantity||0);
                return(
                  <div key={i} className="flex items-center gap-3 bg-slate-50 rounded-lg p-2.5">
                    <div className="flex-1">
                      <p className="font-semibold text-sm text-slate-800">{it.itemName}</p>
                      <p className="text-xs text-slate-400">Requested: {it.quantity} {it.unit} · Already issued: {it.fulfilledQuantity||0}</p>
                    </div>
                    <div>
                      <label className="text-xs text-slate-400 block">Issue qty</label>
                      <input type="number" min="0" max={remaining} className="input w-24 text-center"
                        value={fulfillQty[i]??''} onChange={e=>setFulfillQty({...fulfillQty,[i]:e.target.value})}/>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex gap-3">
              <button onClick={fulfill} disabled={saving} className="btn-primary flex-1 py-2.5">{saving?'Processing...':'📦 Confirm Fulfillment & Print Slip'}</button>
              <button onClick={()=>setShowDetail(null)} className="btn-secondary px-6">Cancel</button>
            </div>
          </div>
        )}
      </SlidePanel>

      {/* ═══ REJECT MODAL ═══ */}
      <SlidePanel isOpen={!!showReject} onClose={()=>setShowReject(null)} title="✕ Reject Indent" size="sm">
        {showReject&&(
          <div>
            <p className="text-sm text-slate-600 mb-3">Reject {showReject.indentNumber}?</p>
            <textarea className="input mb-4" rows={2} value={rejectReason} onChange={e=>setRejectReason(e.target.value)}
              placeholder="Reason for rejection..."/>
            <div className="flex gap-3">
              <button onClick={reject} className="btn-danger flex-1">Reject</button>
              <button onClick={()=>setShowReject(null)} className="btn-secondary px-6">Cancel</button>
            </div>
          </div>
        )}
      </SlidePanel>
    </Layout>
  );
}
